/**
 * V2 Step 2 — Business basics.
 * Fields: fullName, businessName, businessAddress, timezone, bookingUrl.
 * Custom URL field: status pill (auto/checking/available/taken/invalid),
 * reset-to-auto link, live URL preview below.
 */
import * as React from 'react';
import { useState } from 'react';
import { V2C, fontBody, fontMono } from '../shared/tokens';
import { V2Input, V2Select, V2Btn, V2Label, V2InlineMsg } from '../shared/primitives';
import V2Step from '../shared/V2Step';
import { useV2BookingUrlCheck } from '@/hooks/useV2BookingUrlCheck';
import { v2Slugify, V2_SUPPORTED_TIMEZONES } from '@/components/onboarding/onboardingDefaults';
import type { BusinessSignupData } from '@/components/onboarding/magic-steps/BusinessSignup';

interface Props {
  formData: BusinessSignupData;
  update: (patch: Partial<BusinessSignupData>) => void;
  onBack?: () => void;
  onNext: () => void;
  stepIdx: number;
  totalSteps: number;
  animDir: 'forward' | 'back';
  validationError: string;
  userId?: string;
  onBusinessNameChange: (name: string) => void;
}

type PillState = 'auto' | 'checking' | 'available' | 'taken' | 'invalid';

const PILL_MAP: Record<PillState, { bg: string; fg: string; label: string }> = {
  auto:      { bg: V2C.brandSoft, fg: V2C.brand, label: 'Auto' },
  available: { bg: V2C.okBg,      fg: V2C.ok,    label: 'Available' },
  taken:     { bg: V2C.errBg,     fg: V2C.err,   label: 'Taken' },
  invalid:   { bg: V2C.errBg,     fg: V2C.err,   label: 'Invalid' },
  checking:  { bg: V2C.bgAlt,     fg: V2C.mute,  label: '…' },
};

export default function V2BusinessStep({
  formData, update, onBack, onNext, stepIdx, totalSteps, animDir, validationError, userId, onBusinessNameChange,
}: Props) {
  const [urlEdited, setUrlEdited] = useState(false);
  const urlCheck = useV2BookingUrlCheck(formData.bookingUrl, userId);
  const baseSlug = v2Slugify(formData.businessName);

  const pillState: PillState = !urlEdited
    ? 'auto'
    : urlCheck.status === 'checking' ? 'checking'
    : urlCheck.status === 'available' ? 'available'
    : urlCheck.status === 'taken' ? 'taken'
    : urlCheck.status === 'invalid' ? 'invalid'
    : 'auto';

  const pill = PILL_MAP[pillState];
  const ringError = pillState === 'taken' || pillState === 'invalid';

  function handleUrlChange(val: string) {
    setUrlEdited(true);
    update({ bookingUrl: val.toLowerCase().replace(/[^a-z0-9-]/g, '') });
  }

  function resetToAuto() {
    setUrlEdited(false);
    update({ bookingUrl: baseSlug });
  }

  // Block Continue only when URL typed but not confirmed available
  const canContinue = !urlEdited || urlCheck.status === 'idle' || urlCheck.ok;

  return (
    <V2Step
      stepIdx={stepIdx}
      totalSteps={totalSteps}
      title="Tell us about your business"
      subtitle="The essentials clients see, and the URL where they'll find you."
      onBack={onBack}
      animDir={animDir}
    >
      <V2Input
        label="Your full name"
        value={formData.fullName}
        onChange={v => update({ fullName: v })}
        placeholder="Sarah Martinez"
        autoComplete="name"
        autoFocus
        onboardingField="full_name"
      />

      <V2Input
        label="Business name"
        value={formData.businessName}
        onChange={onBusinessNameChange}
        placeholder="The Martinez Studio"
        autoComplete="organization"
        onboardingField="business_name"
      />

      <V2Input
        label="Business address"
        value={formData.businessAddress}
        onChange={v => update({ businessAddress: v })}
        placeholder="123 Main St, Brooklyn, NY 11201"
        autoComplete="street-address"
        onboardingField="business_address"
      />

      <V2Input
        label="Suite / Unit"
        optional
        value={formData.businessSuite ?? ''}
        onChange={v => update({ businessSuite: v })}
        placeholder="Suite 4B (optional)"
        autoComplete="address-line2"
        onboardingField="business_suite"
      />

      <V2Select
        label="Timezone"
        value={formData.timezone}
        onChange={v => update({ timezone: v })}
        options={V2_SUPPORTED_TIMEZONES}
        onboardingField="timezone"
      />

      {/* Custom booking URL */}
      <V2Label>Custom booking URL</V2Label>
      <div style={{ marginBottom: 14 }}>
        {/* Input row */}
        <div style={{
          background: '#fff', borderRadius: 12,
          border: `1.5px solid ${ringError ? V2C.errSoft : V2C.line}`,
          padding: '0 12px', display: 'flex', alignItems: 'center', gap: 6,
          boxShadow: ringError ? `0 0 0 3px ${V2C.errBg}` : 'none',
          transition: 'all 0.18s ease',
        }}>
          <span style={{ fontSize: 14, color: V2C.mute, flexShrink: 0, fontFamily: fontBody }}>
            getfiggy.link/
          </span>
          <input
            value={formData.bookingUrl}
            onChange={e => handleUrlChange(e.target.value)}
            placeholder={baseSlug || 'your-handle'}
            data-onboarding-field="booking_url"
            style={{
              flex: 1, minWidth: 0, border: 'none', outline: 'none', background: 'transparent',
              fontFamily: fontBody, fontSize: 15, fontWeight: 700,
              color: ringError ? V2C.err : V2C.brand,
              padding: '13px 0',
            }}
          />
          {/* Status pill */}
          <span style={{
            fontSize: 10, fontWeight: 800, letterSpacing: 0.6, textTransform: 'uppercase',
            color: pill.fg, background: pill.bg, padding: '4px 8px', borderRadius: 99, flexShrink: 0,
          }}>{pill.label}</span>
        </div>

        {/* Reset to auto-generated */}
        {urlEdited && !ringError && (
          <button onClick={resetToAuto} style={{
            background: 'transparent', border: 'none', cursor: 'pointer',
            fontSize: 11, color: V2C.brand, fontWeight: 600, padding: '6px 0',
            display: 'inline-flex', gap: 4, fontFamily: fontBody,
          }}>
            ↺ Reset to auto-generated · {baseSlug || 'yourname'}
          </button>
        )}

        {/* Status message */}
        {urlCheck.message && (
          <V2InlineMsg kind={
            urlCheck.ok ? 'success'
            : ringError ? 'error'
            : 'hint'
          }>
            {urlCheck.message}
          </V2InlineMsg>
        )}

        {/* Live preview */}
        <div style={{ marginTop: 10, fontFamily: fontMono, fontSize: 11, color: V2C.muteSoft, letterSpacing: 0.5 }}>
          PREVIEW
        </div>
        <div style={{
          marginTop: 4, background: V2C.bgAlt, borderRadius: 10,
          padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 2,
          border: `1px solid ${V2C.line}`,
        }}>
          <span style={{ fontSize: 13, color: V2C.mute, fontFamily: fontBody }}>https://</span>
          <span style={{ fontSize: 13, fontWeight: 700, color: V2C.ink, fontFamily: fontBody }}>getfiggy.link/</span>
          <span style={{
            fontSize: 13, fontWeight: 700, fontFamily: fontBody,
            color: (urlCheck.ok || !urlEdited) ? V2C.brand : V2C.muteSoft,
          }}>
            {formData.bookingUrl || baseSlug || 'yourname'}
          </span>
        </div>
      </div>

      {validationError && (
        <div style={{ color: V2C.err, fontSize: 13, fontWeight: 600, marginBottom: 12, fontFamily: fontBody }}>
          {validationError}
        </div>
      )}

      <div style={{ position: 'sticky', bottom: 0, paddingBottom: 24, paddingTop: 8, background: 'linear-gradient(to bottom, transparent, #F3F3FF 60%)' }}>
        <V2Btn onClick={onNext} disabled={!canContinue}>
          Continue
        </V2Btn>
      </div>
    </V2Step>
  );
}
